/**
 * Hook that owns the Ticket Overview Columns editable table's local row
 * state, validation (Save-only), and serialization to
 * organization.options.ticket_overview_options.
 *
 * Unlike Well Test Overview Columns, this option has no gating feature
 * code and no Decimal Places field. Column Name must additionally be
 * unique (case-insensitive) within the table (WI 40367 AC).
 */
import { useCallback, useRef, useState } from 'react';
// Digits only, comma-separated, at most one space after each comma (not
// zero-or-more), no leading/trailing/double commas.
const ATTRIBUTE_CODE_LIST_PATTERN = /^\d+(, ?\d+)*$/;
export function parseAttributeTypeCodes(text) {
    const trimmed = text.trim();
    if (trimmed === '' || !ATTRIBUTE_CODE_LIST_PATTERN.test(trimmed)) {
        return null;
    }
    return trimmed.split(',').map((part) => parseInt(part.trim(), 10));
}
function storedOption(org) {
    var _a;
    const stored = (_a = org === null || org === void 0 ? void 0 : org.options) === null || _a === void 0 ? void 0 : _a.ticket_overview_options;
    return typeof stored === 'object' && stored !== null ? stored : undefined;
}
function toRow(column) {
    return {
        id: crypto.randomUUID(),
        name: column.name,
        attributeTypeCodeText: (column.attribute_type_code || []).join(', '),
    };
}
function toStoredColumn(row) {
    var _a;
    return {
        name: row.name.trim(),
        attribute_type_code: (_a = parseAttributeTypeCodes(row.attributeTypeCodeText)) !== null && _a !== void 0 ? _a : [],
    };
}
export function useTicketOverviewColumns(org) {
    const initialStoredOption = storedOption(org);
    const hadStoredOptionRef = useRef(initialStoredOption != null);
    const [rows, setRows] = useState(() => { var _a; return ((_a = initialStoredOption === null || initialStoredOption === void 0 ? void 0 : initialStoredOption.columns) !== null && _a !== void 0 ? _a : []).map(toRow); });
    const [errors, setErrors] = useState({});
    const reset = useCallback(() => {
        var _a;
        const stored = storedOption(org);
        hadStoredOptionRef.current = stored != null;
        setRows(((_a = stored === null || stored === void 0 ? void 0 : stored.columns) !== null && _a !== void 0 ? _a : []).map(toRow));
        setErrors({});
    }, [org]);
    const addRow = useCallback(() => {
        setRows((prev) => [...prev, { id: crypto.randomUUID(), name: '', attributeTypeCodeText: '' }]);
    }, []);
    const removeRow = useCallback((id) => {
        setRows((prev) => prev.filter((row) => row.id !== id));
    }, []);
    const updateRow = useCallback((id, field, value) => {
        setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    }, []);
    const moveRow = useCallback((fromIndex, toIndex) => {
        setRows((previous) => {
            if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= previous.length || toIndex >= previous.length) {
                return previous;
            }
            const next = [...previous];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    }, []);
    const validate = useCallback(() => {
        var _a, _b;
        // Uniqueness is judged on the case-insensitive name; the user's original
        // casing is preserved when the column is stored and displayed.
        const nameCounts = {};
        for (const row of rows) {
            const key = row.name.trim().toLowerCase();
            if (key) {
                nameCounts[key] = ((_a = nameCounts[key]) !== null && _a !== void 0 ? _a : 0) + 1;
            }
        }
        const next = {};
        for (const row of rows) {
            const rowErrors = {};
            const trimmedName = row.name.trim();
            if (!trimmedName) {
                rowErrors.name = 'Required';
            }
            else if (((_b = nameCounts[trimmedName.toLowerCase()]) !== null && _b !== void 0 ? _b : 0) > 1) {
                rowErrors.name = 'Must be unique.';
            }
            if (!row.attributeTypeCodeText.trim()) {
                rowErrors.attributeTypeCode = 'Required';
            }
            else if (parseAttributeTypeCodes(row.attributeTypeCodeText) === null) {
                rowErrors.attributeTypeCode = 'Enter digits separated by commas (e.g. 78,206 or 79, 201).';
            }
            if (Object.keys(rowErrors).length > 0) {
                next[row.id] = rowErrors;
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    }, [rows]);
    const toOptionsPatch = useCallback(() => {
        if (rows.length === 0 && !hadStoredOptionRef.current) {
            return {};
        }
        return { ticket_overview_options: { columns: rows.map(toStoredColumn) } };
    }, [rows]);
    return { rows, errors, addRow, removeRow, updateRow, moveRow, validate, reset, toOptionsPatch };
}
